/**
 * Component Edit Dialog - New Implementation
 *
 * Create/edit components with direct field values.
 * Simplified without language dictionary - conditions are stored as direct strings.
 */
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	Typography,
	Chip,
	IconButton,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

// Default conditions to pre-populate for new components or components with no conditions
const DEFAULT_CONDITIONS = ["Good", "Needs Repair", "Missing", "Damaged"];

/**
 * ComponentEditDialog - Modal for creating/editing components
 */
const ComponentEditDialog = ({ open, onClose, onSave, component }) => {
	const { messages } = useIntl();
	const [formData, setFormData] = useState({
		component: "",
		conditions: [],
	});

	const [newCondition, setNewCondition] = useState("");

	useEffect(() => {
		if (component) {
			// Editing existing component - use direct field values
			// Pre-populate with defaults if component has no conditions
			const existingConditions = component.conditions || [];
			setFormData({
				component: component.component || "",
				conditions: existingConditions.length > 0 ? existingConditions : [...DEFAULT_CONDITIONS],
			});
		} else {
			// Creating new component - pre-populate with default conditions
			setFormData({
				component: "",
				conditions: [...DEFAULT_CONDITIONS],
			});
		}
		setNewCondition("");
	}, [component, open]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleAddCondition = () => {
		if (newCondition.trim()) {
			// Add condition as direct string (no UUID/language dictionary)
			setFormData((prev) => ({
				...prev,
				conditions: [...prev.conditions, newCondition.trim()],
			}));
			setNewCondition("");
		}
	};

	const handleRemoveCondition = (index) => {
		setFormData((prev) => ({
			...prev,
			conditions: prev.conditions.filter((_, i) => i !== index),
		}));
	};

	const handleSave = () => {
		onSave({
			component: formData.component,
			conditions: formData.conditions,
		});
		onClose();
	};

	const isFormValid = () => {
		// Require component name and at least 1 condition
		return formData.component.trim() !== "" && formData.conditions.length > 0;
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{component ? (messages.editComponent || "Edit Component") : (messages.addNewComponent || "Add New Component")}</DialogTitle>
			<DialogContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						label={messages.componentName || "Component Name"}
						value={formData.component}
						onChange={(e) => handleChange("component", e.target.value)}
						fullWidth
						placeholder={messages.componentNamePlaceholder || "e.g., Brakes, Tires, Lights"}
						helperText={messages.enterComponentName || "Enter the component name"}
						required
					/>

					<Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
						{messages.conditions || "Conditions"}
					</Typography>
					<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
						<TextField
							label={messages.condition || "Condition"}
							value={newCondition}
							onChange={(e) => setNewCondition(e.target.value)}
							onKeyPress={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleAddCondition();
								}
							}}
							placeholder={messages.conditionPlaceholder || "e.g., Missing, Expired, Leaking"}
							fullWidth
							size="small"
							helperText={messages.pressEnterOrClickToAdd || "Press Enter or click + to add"}
						/>
						<IconButton onClick={handleAddCondition} color="primary" sx={{ mt: 0.5 }}>
							<AddIcon />
						</IconButton>
					</Box>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
						{formData.conditions.map((condition, index) => (
							<Chip
								key={index}
								label={condition}
								onDelete={() => handleRemoveCondition(index)}
								deleteIcon={<DeleteIcon />}
								size="small"
								sx={{
									backgroundColor: "#1976d2",
									color: "#fff",
									"& .MuiChip-deleteIcon": {
										color: "rgba(255, 255, 255, 0.7)",
										"&:hover": {
											color: "#fff",
										},
									},
								}}
							/>
						))}
						{formData.conditions.length === 0 && (
							<Typography variant="body2" color="error">
								{messages.atLeastOneConditionRequired || "At least one condition is required"}
							</Typography>
						)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{messages.cancel || "Cancel"}</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					color="primary"
					disabled={!isFormValid()}
				>
					{component ? (messages.saveChanges || "Save Changes") : (messages.addComponent || "Add Component")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ComponentEditDialog;
